/*
  Fills the `[product sku="..."]` placeholders left by snippets/shortcodes.liquid.

  Liquid can only look a product up by handle, so the SKU is resolved with one
  Section Rendering API request per SKU:

    /search?q=<SKU>&type=product&section_id=shortcode-product

  The response is the real theme product card, so the markup stays identical to
  the rest of the store. Results are cached per session and placeholders are
  only requested once they come close to the viewport.
*/
(function () {
  'use strict';
  
  var SECTIONS = {
    card: 'shortcode-product',
    spotlight: 'shortcode-product-spotlight',
  };
  var PLACEHOLDER_SELECTOR = '[data-shortcode-product]';

  var CACHE_PREFIX = 'myratna:shortcode-product:v3:';
  var REQUEST_TIMEOUT = 10000;
  var MISSING_SECTION = 'sections/shortcode-product.liquid did not render. Is that file in this theme?';

  function canHydrate() {
    return !!(window.fetch && window.Promise);
  }

  if (!document.querySelectorAll) return;

  var inflight = {};

  function closest(element, className) {
    for (var node = element; node && node.nodeType === 1; node = node.parentNode) {
      if (node.classList && node.classList.contains(className)) return node;
    }
    return null;
  }

  function searchUrl(element) {
    var block = closest(element, 'shortcode-product');
    var root = block && block.getAttribute('data-search-url');
    if (root) return root;
    if (window.theme && window.theme.routes && window.theme.routes.root_url) {
      return window.theme.routes.root_url.replace(/\/$/, '') + '/search';
    }
    return '/search';
  }

  function cacheKey(sku, layout) {
    return CACHE_PREFIX + layout + ':' + sku;
  }

  function readCache(sku, layout) {
    try {
      return window.sessionStorage.getItem(cacheKey(sku, layout));
    } catch (error) {
      return null;
    }
  }

  function writeCache(sku, layout, html) {
    try {
      window.sessionStorage.setItem(cacheKey(sku, layout), html);
    } catch (error) {
    }
  }

  function extractCard(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var card = parsed.querySelector('[data-shortcode-card]');
    if (card) return { rendered: true, html: card.innerHTML.trim() };
    if (parsed.querySelector('[data-shortcode-empty]')) return { rendered: true, html: '' };
    return { rendered: false, html: '' };
  }

  function fetchCard(sku, layout, element) {
    var key = layout + ':' + sku;
    if (inflight[key]) return inflight[key];

    var cached = readCache(sku, layout);
    if (cached !== null) {
      inflight[key] = Promise.resolve(cached);
      return inflight[key];
    }

    var url =
      searchUrl(element) +
      '?q=' +
      encodeURIComponent(sku) +
      '&type=product&options[fields]=variants.sku&section_id=' +
      (SECTIONS[layout] || SECTIONS.card);

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = window.setTimeout(function () {
      if (controller) controller.abort();
    }, REQUEST_TIMEOUT);

    inflight[key] = fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        if (response.status === 404) throw new Error(MISSING_SECTION + ' (404 from ' + url + ')');
        if (!response.ok) throw new Error('Shortcode product request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        window.clearTimeout(timer);
        var result = extractCard(html);
        if (!result.rendered) throw new Error(MISSING_SECTION + ' (requested ' + url + ')');
        if (result.html) writeCache(sku, layout, result.html);
        return result.html;
      })
      .catch(function (error) {
        window.clearTimeout(timer);
        delete inflight[key];
        throw error;
      });

    return inflight[key];
  }

  function drop(node) {
    if (!node) return;
    if (node.remove) node.remove();
    else if (node.parentNode) node.parentNode.removeChild(node);
  }

  function notFound(element, sku, reason) {
    var parent = element.parentNode;
    var block = closest(element, 'shortcode-product');

    if (window.Shopify && window.Shopify.designMode) {
      var notice = document.createElement('p');
      notice.className = 'shortcode-notice';
      notice.textContent =
        reason || 'Shortcode: no product matches the SKU ' + sku + '.';
      if (parent) parent.replaceChild(notice, element);
      return;
    }

    drop(element);
    if (block && !block.querySelector('.shortcode-product__item')) drop(block);
  }

  function hydrate(element) {
    var sku = (element.getAttribute('data-sku') || '').trim();
    var layout = element.getAttribute('data-layout') === 'spotlight' ? 'spotlight' : 'card';

    element.removeAttribute('data-shortcode-product');

    if (!sku) {
      notFound(element, sku);
      return;
    }

    fetchCard(sku, layout, element)
      .then(function (card) {
        if (!card) {
          notFound(element, sku);
          return;
        }
        element.innerHTML = card;
        element.classList.add('is-loaded');
      })
      .catch(function (error) {
        var message = (error && error.message) || String(error);
        console.error('[shortcodes] SKU ' + sku + ': ' + message);
        notFound(element, sku, message.indexOf('shortcode-product.liquid') > -1 ? message : null);
      });
  }

  function observe(elements) {
    if (!('IntersectionObserver' in window)) {
      elements.forEach(hydrate);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          hydrate(entry.target);
        });
      },
      { rootMargin: '400px 0px' }
    );

    elements.forEach(function (element) {
      observer.observe(element);
    });
  }

  function centreArrows(slider) {
    var media = slider.querySelector('.card-product__media');
    var arrow = slider.querySelector('.splide__arrow');
    if (!media || !arrow) return;

    var base = arrow.offsetParent;
    if (!base) return;

    var mediaRect = media.getBoundingClientRect();

    if (mediaRect.height <= 0) return;

    var baseRect = base.getBoundingClientRect();
    var borderTop = parseFloat(getComputedStyle(base).borderTopWidth) || 0;
    var top = mediaRect.top - baseRect.top - borderTop + mediaRect.height / 2;

    slider.style.setProperty('--sc-arrow-top', top.toFixed(1) + 'px');
  }

  function watchArrows(root) {
    var sliders = Array.prototype.slice.call(
      (root || document).querySelectorAll('.shortcode-collection .splide-slider')
    );

    sliders.forEach(function (slider) {
      centreArrows(slider);

      var media = slider.querySelector('.card-product__media');
      if (!media) return;

      if (typeof ResizeObserver === 'function') {
        var observer = new ResizeObserver(function () {
          centreArrows(slider);
        });

        observer.observe(media);
        observer.observe(slider);

        return;
      }

      window.addEventListener('resize', function () {
        centreArrows(slider);
      });
      window.addEventListener('load', function () {
        centreArrows(slider);
      });
    });
  }

  function init(root) {
    watchArrows(root);

    if (!canHydrate()) return;

    var elements = Array.prototype.slice.call(
      (root || document).querySelectorAll(PLACEHOLDER_SELECTOR)
    );
    if (elements.length) observe(elements);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();